import type { DocumentInfo } from '@/types';
import {
  deleteDraftClaimDocument as deleteDraftClaimDocumentApi,
  getDraftClaimDocuments,
} from '@/utils/claims';
import { useShallow } from 'zustand/react/shallow';
import { createWithDevtools } from '../utils';

interface ClaimDocumentsStateProps {
  error: Error | null;
  hasError: boolean;
  isLoading: boolean;
  documents: DocumentInfo[];
  deletingDocumentIds: string[];
  documentErrorsById: Record<string, Error>;
}

interface ClaimDocumentsStateActions {
  resetClaimDocumentsStatus: () => void;
  resetClaimDocumentsData: () => void;
  fetchDraftClaimDocuments: (claimId: string) => Promise<void>;
  deleteDraftClaimDocument: (draftClaimId: string, documentId: string) => Promise<void>;
}

type ClaimDocumentsState = ClaimDocumentsStateProps & ClaimDocumentsStateActions;

const initialStateProps: ClaimDocumentsStateProps = {
  error: null,
  hasError: false,
  isLoading: true,
  documents: [],
  deletingDocumentIds: [],
  documentErrorsById: {},
};

const useStore = createWithDevtools<ClaimDocumentsState>(
  set => ({
    ...initialStateProps,
    resetClaimDocumentsStatus: () => {
      const { error, hasError, isLoading } = initialStateProps;
      set({ error, hasError, isLoading });
    },
    resetClaimDocumentsData: () => {
      const { documents, deletingDocumentIds, documentErrorsById } = initialStateProps;
      set({ documents, deletingDocumentIds, documentErrorsById });
    },
    fetchDraftClaimDocuments: async draftClaimId => {
      set({ error: null, hasError: false, isLoading: true });

      try {
        const documents = await getDraftClaimDocuments(draftClaimId);
        set({ documents, isLoading: false });
      } catch (error) {
        set({ isLoading: false, hasError: true, error: error as Error });
      }
    },
    deleteDraftClaimDocument: async (draftClaimId, documentId) => {
      set(state => {
        const { [documentId]: _, ...newDocumentErrorsById } = state.documentErrorsById;

        return {
          error: null,
          hasError: false,
          deletingDocumentIds: [...state.deletingDocumentIds, documentId],
          documentErrorsById: newDocumentErrorsById,
        };
      });

      try {
        await deleteDraftClaimDocumentApi(draftClaimId, documentId);
        set(state => ({
          documents: state.documents.filter(document => document.documentId !== documentId),
          deletingDocumentIds: state.deletingDocumentIds.filter(id => id !== documentId),
        }));
      } catch (error) {
        set(state => ({
          hasError: true,
          error: error as Error,
          deletingDocumentIds: state.deletingDocumentIds.filter(id => id !== documentId),
          documentErrorsById: { ...state.documentErrorsById, [documentId]: error as Error },
        }));
      }
    },
  }),
  'ClaimDocumentsStore',
);

// Memoized version of the store, using 'selector' with 'useShallow()'.
const useClaimDocumentsStore = <T>(selector: (state: ClaimDocumentsState) => T) => {
  return useStore(useShallow(selector));
};

const getClaimDocumentsState = () => {
  return useStore.getState();
};

const selectClaimDocumentsActions = (state: ClaimDocumentsState) => {
  return {
    resetClaimDocumentsStatus: state.resetClaimDocumentsStatus,
    resetClaimDocumentsData: state.resetClaimDocumentsData,
    fetchDraftClaimDocuments: state.fetchDraftClaimDocuments,
    deleteDraftClaimDocument: state.deleteDraftClaimDocument,
  };
};

const selectCompletedClaimDocuments = (state: ClaimDocumentsState) => {
  return state.documents.filter(document => document.documentStatus === 'complete');
};

const selectClaimDocumentsStatus = (state: ClaimDocumentsState) => {
  return { error: state.error, hasError: state.hasError, isLoading: state.isLoading };
};

const selectIsDocumentDeleting = (documentId: string) => (state: ClaimDocumentsState) => {
  return state.deletingDocumentIds.includes(documentId);
};

const selectDocumentDeleteError = (documentId: string) => (state: ClaimDocumentsState) => {
  return state.documentErrorsById[documentId];
};

export {
  getClaimDocumentsState,
  selectClaimDocumentsActions,
  selectClaimDocumentsStatus,
  selectCompletedClaimDocuments,
  selectDocumentDeleteError,
  selectIsDocumentDeleting,
  useClaimDocumentsStore,
};
